import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
    Shield, Cpu, Lock, Terminal, Activity, Zap, HardDrive, Wifi, EyeOff,
    Check, ChevronDown, ChevronRight, ArrowRight, MessageSquare, BarChart2,
    Clock, DollarSign, Users, Star, AlertTriangle, Wrench, Calendar,
    FileText, Phone, TrendingUp, Package, Map
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

// ─── THE STORY ────────────────────────────────────────────────────────────────
// StoryBrand arc + Made-to-Stick SUCCESs + Contagious STEPPS
// 1. Dawn Scene: the relatable moment (Emotion, Stories)
// 2. The Problem: name the villain concretely (Unexpected, Concrete)
// 3. What Life Looks Like With an Agent: the transformation (Simple, Stories)
// 4. Your Agent Ecosystem: 22 agent glimpses as "what's possible" (Practical Value, Social Currency)
// 5. Old-School Automations vs. Agent Power: marketplace agentification (Concrete, Unexpected)
// 6. Security & Architecture: the proof (Credible)
// 7. The Comparison Table: close objections (Credible, Concrete)
// 8. Final CTA: action (Emotional)
// ─────────────────────────────────────────────────────────────────────────────

const WhyBuyPage = () => {
    const headerRef = useRef(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from('.header-reveal', {
                y: 50,
                opacity: 0,
                duration: 1,
                stagger: 0.15,
                ease: 'power3.out'
            });

            // Scroll-triggered fade-ups for all content sections
            gsap.utils.toArray('.scroll-reveal').forEach(el => {
                gsap.from(el, {
                    y: 30,
                    opacity: 0,
                    duration: 0.9,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: el,
                        start: 'top 85%',
                    }
                });
            });
        });
        return () => ctx.revert();
    }, []);

    return (
        <div className="pt-28 pb-24 bg-[#0A0A14] text-[#F0EFF4] selection:bg-[#7B61FF] selection:text-[#F0EFF4]">

            {/* ═══════════════════════════════════════════════════════════
                SECTION 1: THE DAWN SCENE
                Made-to-Stick: Concrete + Emotional + Stories
                Contagious: Emotion (high-arousal identification) + Stories
                ═══════════════════════════════════════════════════════════ */}
            <section className="max-w-7xl mx-auto px-6 md:px-16 mb-32">
                <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    {/* Story Copy */}
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-data font-semibold tracking-widest text-[#7B61FF] mb-6 header-reveal">
                            <Cpu className="w-4 h-4" />
                            WHY ROOFCLAW EXISTS
                        </div>

                        {/* The story hook — concrete, specific, relatable */}
                        <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-8 leading-tight header-reveal">
                            It's 5:30 AM.{' '}
                            <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal">
                                Your agent is already working.
                            </span>
                        </h1>

                        {/* The story — uses all 3 story plot types */}
                        <div className="space-y-5 text-[#F0EFF4]/70 text-lg leading-relaxed font-light header-reveal">
                            <p>
                                It's Tuesday. Dallas got hit with a Category 2 hail storm overnight. By 5:30 AM, 847 homeowners have already searched "roof damage" on their phones.
                            </p>
                            <p>
                                Your competitors won't open until 8. Their CSR unlocks the door, makes coffee, and starts manually checking the weather app.
                            </p>
                            <p className="text-[#F0EFF4] font-normal">
                                Your RoofClaw agent was monitoring hail trail data at 3 AM. It already filed 14 leads into HubSpot, sent 14 personalized SMS messages via JustCall, and scheduled 6 inspection appointments in Zuper.
                            </p>
                            <p className="text-[#F0EFF4]/70">
                                You wake up to notifications — not problems.
                            </p>
                        </div>

                        {/* Social Currency: "Not everyone knows this" */}
                        <div className="mt-8 p-4 border-l-4 border-[#7B61FF] bg-[#7B61FF]/5 rounded-r-2xl header-reveal">
                            <p className="text-sm text-[#7B61FF] font-data font-bold tracking-widest uppercase mb-1">The Timing Edge</p>
                            <p className="text-[#F0EFF4]/80 text-sm">
                                The average storm restoration contractor responds to a hail lead in <strong className="text-[#F0EFF4]">4.5 hours</strong>. The probability of qualifying that lead drops <strong className="text-[#F0EFF4]">80%</strong> if you don't respond within 5 minutes. Your agent <em>never</em> misses 5 minutes.
                            </p>
                        </div>
                    </div>

                    {/* Hero Image */}
                    <div className="header-reveal">
                        <div className="relative rounded-[2.5rem] overflow-hidden border border-[#7B61FF]/20 shadow-[0_0_60px_rgba(123,97,255,0.1)]">
                            <img
                                src="/images/generated/monster_hero_dawn.png"
                                alt="Roofing contractor at 5:30 AM with RoofClaw agent active on Mac Mini"
                                className="w-full object-cover"
                            />
                            {/* Time badge overlay */}
                            <div className="absolute bottom-6 left-6 bg-[#0A0A14]/90 backdrop-blur-xl border border-[#7B61FF]/30 rounded-2xl px-5 py-3">
                                <div className="font-data text-[#7B61FF] text-xs font-bold uppercase tracking-widest mb-1">Agent Activity</div>
                                <div className="font-data text-[#F0EFF4] text-sm">
                                    <span className="text-[#10B981]">●</span> 14 leads filed &nbsp;·&nbsp; 6 appointments set
                                </div>
                                <div className="font-data text-[#F0EFF4]/40 text-xs mt-1">While you slept · 3:14 AM – 5:29 AM</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════
                SECTION 2: THE VILLAIN — NAMING THE PROBLEM
                StoryBrand: External Problem + Internal Frustration + Philosophical Problem
                Made-to-Stick: Unexpected + Concrete
                ═══════════════════════════════════════════════════════════ */}
            <section className="bg-[#18181B] py-24 mx-4 md:mx-8 rounded-[3rem] mb-24 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03] bg-[url('data:image/svg+xml,%3Csvg viewBox=%220 0 200 200%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cfilter id=%22noiseFilter%22%3E%3CfeTurbulence type=%22fractalNoise%22 baseFrequency=%220.65%22 numOctaves=%223%22 stitchTiles=%22stitch%22/%3E%3C/filter%3E%3Crect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23noiseFilter)%22/%3E%3C/svg%3E')] mix-blend-overlay pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-6 md:px-16">
                    {/* Unexpected opener: most people don't know this about their own day */}
                    <div className="text-center mb-16 scroll-reveal">
                        <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">The Problem You Can't See</p>
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Most roofing owners are running{' '}
                            <span className="font-drama italic text-[#7B61FF]">4 businesses at once</span>
                            {' '}using their own brain as the server.
                        </h2>
                        <p className="text-[#F0EFF4]/60 text-lg max-w-2xl mx-auto">
                            And unlike a server, your brain has uptime problems. It goes to sleep. It gets distracted. It cannot process 847 leads simultaneously.
                        </p>
                    </div>

                    {/* The chaos vs. control visual — split image */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center scroll-reveal">
                        <div className="relative rounded-[2rem] overflow-hidden border border-[#7B61FF]/20">
                            <img
                                src="/images/generated/monster_chaos_vs_control.png"
                                alt="Chaos of running a roofing office vs. calm with RoofClaw AI agent"
                                className="w-full object-cover"
                            />
                        </div>

                        <div className="space-y-8">
                            {/* Concrete specifics — the curse of knowledge stripped out */}
                            {[
                                {
                                    icon: AlertTriangle,
                                    color: '#EF4444',
                                    title: 'The 5-Minute Window',
                                    body: 'A lead that doesn\'t get a response in 5 minutes is 80% less likely to convert. You get 200+ leads during a storm week. You cannot respond to all of them in 5 minutes. Your agent can.'
                                },
                                {
                                    icon: DollarSign,
                                    color: '#F59E0B',
                                    title: 'The Subscription Trap',
                                    body: 'The average roofing company uses 12+ SaaS tools. None of them talk to each other without you being the translator. You\'re paying $3,000+/month in subscriptions to create your own bottleneck.'
                                },
                                {
                                    icon: HardDrive,
                                    color: '#7B61FF',
                                    title: 'The Data Hostage Problem',
                                    body: 'Every time you type a customer\'s name into HubSpot or a note into JobNimbus, that data is sitting on someone else\'s server. You don\'t own it. You rent access to it.'
                                },
                            ].map((item, i) => {
                                const IconEl = item.icon;
                                return (
                                    <div key={i} className="flex gap-5">
                                        <div className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center mt-1" style={{ backgroundColor: `${item.color}15` }}>
                                            <IconEl size={18} style={{ color: item.color }} />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-[#F0EFF4] text-lg mb-1">{item.title}</h4>
                                            <p className="text-[#F0EFF4]/60 text-sm leading-relaxed">{item.body}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════
                SECTION 3: THE TRANSFORMATION — WHAT LIFE LOOKS LIKE
                StoryBrand: Success (what life looks like after the plan)
                Made-to-Stick: Simple + Concrete + Emotional + Stories
                Contagious: Emotion (awe) + Stories
                ═══════════════════════════════════════════════════════════ */}
            <section className="max-w-7xl mx-auto px-6 md:px-16 mb-32">
                <div className="scroll-reveal text-center mb-16">
                    <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">The Other Side</p>
                    <h2 className="text-4xl md:text-5xl font-bold mb-6">
                        What does it actually <em className="font-drama text-[#7B61FF] not-italic">feel</em> like<br />to have an OpenClaw agent?
                    </h2>
                    <p className="text-[#F0EFF4]/60 text-lg max-w-xl mx-auto">
                        Not the tech specs. The morning. The texts. The moment it clicks.
                    </p>
                </div>

                {/* Story: A Day in the Life — narrative beats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
                    {[
                        {
                            time: '6:45 AM',
                            icon: MessageSquare,
                            headline: 'You check Telegram over coffee.',
                            story: '"Morning brief: 3 new inbound leads from overnight. 2 scheduled for tomorrow. 1 estimate follow-up pending approval." You type: "Send the estimate." It\'s done before you finish your cup.',
                        },
                        {
                            time: '11:30 AM',
                            icon: Map,
                            headline: 'A crew doesn\'t show up on time.',
                            story: 'Your agent detects the job start was missed. It pulls the crew supervisor\'s number, sends an automated status check, and alerts you only if there\'s no response in 10 minutes. You\'re on a roof inspection. You don\'t even notice.',
                        },
                        {
                            time: '4:15 PM',
                            icon: Star,
                            headline: 'A job wraps. Reviews come in.',
                            story: '"Job at 1847 Oakway is marked complete in Zuper." 2 minutes later, the homeowner gets a personalized SMS asking for a Google review. By the time you drive back to the office, the review is already posted.',
                        },
                    ].map((item, i) => {
                        const IconEl = item.icon;
                        return (
                            <div key={i} className="scroll-reveal bg-[#18181B] border border-[#7B61FF]/20 rounded-[2rem] p-8 flex flex-col">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="bg-[#7B61FF]/15 p-2 rounded-xl">
                                        <IconEl size={18} className="text-[#7B61FF]" />
                                    </div>
                                    <span className="font-data text-[#7B61FF] text-xs font-bold tracking-widest">{item.time}</span>
                                </div>
                                <h3 className="font-bold text-[#F0EFF4] text-lg mb-3">{item.headline}</h3>
                                <p className="text-[#F0EFF4]/60 text-sm leading-relaxed italic flex-1">"{item.story}"</p>
                            </div>
                        );
                    })}
                </div>

                {/* The deal-closed image with emotional caption */}
                <div className="scroll-reveal grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
                    <div className="lg:col-span-2 relative rounded-[2rem] overflow-hidden border border-[#7B61FF]/20 shadow-[0_0_40px_rgba(123,97,255,0.1)]">
                        <img
                            src="/images/generated/monster_deal_closed.png"
                            alt="Roofing contractor seeing deal closed notification on HubSpot"
                            className="w-full object-cover"
                        />
                    </div>
                    <div className="lg:col-span-3">
                        <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">The Real Shift</p>
                        <h3 className="text-3xl md:text-4xl font-bold mb-6">
                            You stop being the{' '}
                            <span className="font-drama italic text-[#7B61FF]">coordinator</span>.
                            <br />You start being the{' '}
                            <span className="font-drama italic text-[#F0EFF4]">commander</span>.
                        </h3>
                        <p className="text-[#F0EFF4]/70 text-lg leading-relaxed mb-6">
                            The mental shift is hard to describe until you live it. There's a moment — usually around week 2 — when you realize you haven't manually filed a lead in days. You haven't nagged a crew about a job update. You haven't personally sent a review request.
                        </p>
                        <p className="text-[#F0EFF4]/70 text-lg leading-relaxed">
                            You've been running the business. The agent has been running the paperwork.
                        </p>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════
                SECTION 4: STORM SURGE — THE POWER MOMENT
                Made-to-Stick: Concrete (specific numbers) + Unexpected + Emotional
                Contagious: Emotion (awe at scale) + Practical Value
                ═══════════════════════════════════════════════════════════ */}
            <section className="relative mb-24 overflow-hidden rounded-[3rem] mx-4 md:mx-8">
                <img
                    src="/images/generated/monster_storm_surge.png"
                    alt="Aerial view of hail-damaged neighborhood — your agent is already on it"
                    className="w-full h-[600px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A14] via-[#0A0A14]/70 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-12 md:p-20 max-w-4xl">
                    <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">The Defining Moment for Storm Roofers</p>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                        Hail hits at midnight.{' '}
                        <span className="font-drama italic text-[#7B61FF]">
                            Your agent is awake.
                        </span>
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                            { number: '< 1 min', label: 'Agent lead response time' },
                            { number: '80%', label: 'Drop in lead conversion after 5 min delay' },
                            { number: '24/7', label: 'Storm monitoring coverage' },
                        ].map((stat, i) => (
                            <div key={i} className="bg-[#0A0A14]/80 backdrop-blur-md border border-[#7B61FF]/20 rounded-2xl p-4">
                                <div className="font-drama text-[#7B61FF] text-4xl mb-1">{stat.number}</div>
                                <div className="text-[#F0EFF4]/60 text-xs font-data uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════
                SECTION 5: YOUR AGENT ECOSYSTEM — THE POSSIBLE FUTURE
                Contagious: Social Currency (insider knowledge, you know what's possible)
                           Practical Value (specific, actionable ideas)
                Made-to-Stick: Concrete + Simple (one idea per card)
                Design: hint at depth without giving away recipes
                ═══════════════════════════════════════════════════════════ */}
            <AgentEcosystem />

            {/* ═══════════════════════════════════════════════════════════
                SECTION 6: AUTOMATION MARKETPLACE → AGENTIFICATION
                Contagious: Practical Value (massive value burst, shareable)
                Made-to-Stick: Unexpected (what if your $49/mo Zapier zap was an agent?)
                ═══════════════════════════════════════════════════════════ */}
            <AgentificationSection />

            {/* ═══════════════════════════════════════════════════════════
                SECTION 7: THE ARCHITECTURE — SECURITY CREDIBILITY
                Made-to-Stick: Credible (Sinatra Test)
                StoryBrand: Plan (inspires confidence)
                ═══════════════════════════════════════════════════════════ */}
            <section className="bg-[#0A0A14] text-[#F0EFF4] py-24 rounded-[3rem] mx-4 md:mx-8 mb-24 relative overflow-hidden border border-[#7B61FF]/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-20 relative z-10">
                    <div className="scroll-reveal">
                        <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">Built for Builders Who Value Data</p>
                        <h2 className="text-4xl lg:text-5xl font-bold mb-6">Invisible to the<br /><span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl lg:text-7xl">Open Internet.</span></h2>
                        <p className="text-lg text-[#F0EFF4]/70 leading-relaxed mb-10">
                            When you expose a database to the internet, it takes an average of 14 minutes for the first automated scan to find it. The next 20 minutes are spent trying to brute-force it.
                            <br /><br />
                            RoofClaw systems are entirely walled off. We install, configure, and then deliberately remove our own ability to access your machine. Once it ships, your data belongs to nobody but you.
                        </p>
                        <div className="space-y-6">
                            {[
                                {
                                    icon: EyeOff,
                                    title: 'Tailscale Subnet Routing',
                                    body: 'Your agent sits behind a cryptographic mesh network. It\'s completely isolated from the public web while maintaining lightning-fast access to your internal tools.'
                                },
                                {
                                    icon: Lock,
                                    title: 'No SSH Access on Delivery',
                                    body: 'Once your machine ships, we disable SSH access entirely. Not because we have to — because we want you to know it\'s impossible for us to log in.'
                                },
                                {
                                    icon: Shield,
                                    title: 'Local Data Sovereignty',
                                    body: 'Run powerful models like Llama locally without sending your customer PII to external APIs. Your customer database never leaves the machine on your desk.'
                                },
                            ].map((item, i) => {
                                const IconEl = item.icon;
                                return (
                                    <div key={i} className="flex gap-4">
                                        <div className="mt-1 bg-[#18181B] p-2 rounded-xl h-fit border border-[#F0EFF4]/10">
                                            <IconEl className="w-5 h-5 text-[#7B61FF]" />
                                        </div>
                                        <div>
                                            <h4 className="text-xl font-bold mb-2">{item.title}</h4>
                                            <p className="text-[#F0EFF4]/70 text-sm leading-relaxed">{item.body}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Terminal visual */}
                    <div className="scroll-reveal bg-[#18181B] rounded-[2rem] border border-[#F0EFF4]/10 p-8 flex flex-col justify-between min-h-[500px] relative overflow-hidden group">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#7B61FF] to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></div>
                        <div className="flex items-center justify-between border-b border-[#F0EFF4]/10 pb-4 mb-8">
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-[#10B981]" />
                                <span className="font-data text-xs tracking-widest text-[#F0EFF4]/60">TAILSCALE ENCRYPTION ACTIVE</span>
                            </div>
                            <span className="font-data text-xs text-[#7B61FF]">SECURE</span>
                        </div>
                        <div className="flex-1 font-data text-sm text-[#F0EFF4]/50 leading-loose">
                            <div className="mb-2"><span className="text-[#7B61FF]">root@roofclaw:~#</span> status --mesh</div>
                            <div className="text-[#10B981] mb-6">[OK] Node connected via WireGuard. IP space hidden.</div>
                            <div className="mb-2"><span className="text-[#7B61FF]">root@roofclaw:~#</span> ping hubspot.local</div>
                            <div>64 bytes from 100.91.x.x: icmp_seq=1 ttl=64 time=0.041 ms</div>
                            <div>64 bytes from 100.91.x.x: icmp_seq=2 ttl=64 time=0.038 ms</div>
                            <div className="text-[#10B981] mt-2">[OK] HubSpot bridge nominal</div>
                            <div className="mt-4 mb-2"><span className="text-[#7B61FF]">root@roofclaw:~#</span> check --ssh-access</div>
                            <div className="text-[#10B981]">[OK] SSH disabled. No remote admin access exists.</div>
                            <div className="text-[#F0EFF4]/30 mt-4 text-xs">Your machine. Your data. Nobody else's keys.</div>
                        </div>
                        <div className="mt-8 pt-4 border-t border-[#F0EFF4]/10 flex justify-between text-xs font-mono text-[#F0EFF4]/40">
                            <span>UPTIME: 99.99%</span>
                            <span>AES-256</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════
                SECTION 8: COMPARISON TABLE
                Made-to-Stick: Credible + Concrete
                ═══════════════════════════════════════════════════════════ */}
            <section className="bg-[#0A0A14] py-24 mb-24 border-y border-[#7B61FF]/20">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
                        <div className="max-w-2xl scroll-reveal">
                            <h2 className="text-3xl md:text-4xl font-bold mb-4">The Architecture <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-4xl md:text-5xl">Spectrum.</span></h2>
                            <p className="text-[#F0EFF4]/70">Why we explicitly avoid cloud VMs and fragmented SaaS subscriptions in favor of absolute physical ownership.</p>
                        </div>
                    </div>
                    <div className="scroll-reveal overflow-x-auto pb-8">
                        <table className="w-full min-w-[800px] text-left border-collapse">
                            <thead>
                                <tr className="border-b-2 border-[#7B61FF]/30">
                                    <th className="py-4 px-6 font-bold text-[#F0EFF4]/50 uppercase tracking-widest text-xs">Dimension</th>
                                    <th className="py-4 px-6 font-bold text-[#F0EFF4] text-lg">DIY / YouTube Courses</th>
                                    <th className="py-4 px-6 font-bold text-[#F0EFF4] text-lg">Cloud VM / AWS</th>
                                    <th className="py-4 px-6 font-bold text-[#7B61FF] text-lg bg-[#7B61FF]/10 rounded-t-xl">RoofClaw Systems</th>
                                </tr>
                            </thead>
                            <tbody className="text-sm border-b border-[#7B61FF]/20">
                                {[
                                    ['Time to First Agent', 'Weeks. Broken APIs, overwhelming forums, zero support.', 'Days. Requires Linux routing, AWS IAM, Docker knowledge.', '1 Day after delivery. Plug in and command.'],
                                    ['Security Posture', 'Extreme Risk. Default Docker ports exposed to public scanners.', 'High Risk. One misconfigured S3 bucket exposes your customer list.', 'Absolute. Dark subnet via Tailscale. SSH disabled on shipping.'],
                                    ['Data Sovereignty', 'High — but catastrophic loss risk without professional backups.', 'Low. Amazon/Azure technically holds the underlying hardware keys.', 'Total. You own the metal holding the intelligence.'],
                                    ['Ongoing Cost', '$500-$1,000 for courses + massive hidden operator time cost.', 'Infinite. Creeping monthly compute + egress charges scale with usage.', 'One-time capital investment. $10,000. No subscriptions.'],
                                    ['Expert Support', 'Forums, subreddits, YouTubers who may not know your stack.', 'AWS support tickets at $100+/hour premium tiers.', 'Winning Wednesday — live weekly group coaching. Forever.'],
                                ].map((row, i) => (
                                    <tr key={i} className="border-b border-[#F0EFF4]/10 hover:bg-[#F0EFF4]/5 transition-colors">
                                        <td className="py-6 px-6 font-semibold uppercase tracking-wider text-xs text-[#F0EFF4]/50">{row[0]}</td>
                                        <td className="py-6 px-6 text-[#F0EFF4]/70">{row[1]}</td>
                                        <td className="py-6 px-6 text-[#F0EFF4]/70">{row[2]}</td>
                                        <td className="py-6 px-6 font-medium text-[#F0EFF4] bg-[#7B61FF]/10">{row[3]}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* ═══════════════════════════════════════════════════════════
                FINAL CTA
                StoryBrand: Call to Action
                Made-to-Stick: Emotional (individual success)
                ═══════════════════════════════════════════════════════════ */}
            <section className="max-w-4xl mx-auto px-6 text-center mb-24 scroll-reveal">
                <h2 className="text-4xl font-bold mb-6">
                    Stop being the server.<br />Start being the <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-5xl">commander.</span>
                </h2>
                <p className="text-[#F0EFF4]/70 mb-10 max-w-xl mx-auto text-lg">
                    One machine. One investment. An agent that works while you sleep, closes while you're on the roof, and scales when the storms hit hardest.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Link
                        to="/pricing"
                        className="inline-flex items-center justify-center gap-2 px-10 py-5 rounded-full bg-[#7B61FF] text-[#F0EFF4] font-bold text-lg shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.7)] hover:scale-[1.03] transition-all"
                    >
                        See Pricing <ArrowRight size={20} />
                    </Link>
                    <Link
                        to="/"
                        className="inline-flex items-center justify-center gap-2 px-8 py-5 rounded-full border border-[#F0EFF4]/20 text-[#F0EFF4]/80 hover:bg-[#F0EFF4]/10 transition-all text-lg"
                    >
                        ← Back to Overview
                    </Link>
                </div>
            </section>

        </div>
    );
};

// ─── AGENT ECOSYSTEM SECTION ─────────────────────────────────────────────────
// Shows all 22 agents as "glimpses" — tantalizing without being a tutorial
// Uses Contagious: Social Currency ("look what you could know") + Practical Value

const agentCategories = [
    {
        category: 'Sales',
        icon: TrendingUp,
        color: '#10B981',
        agents: [
            { id: '001', name: 'Voice Lead Intake', tagline: 'Every storm surge lead captured in under 60 seconds. 24/7.', tease: 'Integrates with JustCall + HubSpot. Handles 200 simultaneous inbound calls without a single missed lead.' },
            { id: '002', name: 'Presentation Recorder', tagline: 'Every sales pitch recorded, transcribed, and summarized automatically.', tease: 'AI transcription extracts objections, auto-generates personalized follow-up emails, and syncs to HubSpot.' },
            { id: '003', name: 'Sales Closure', tagline: 'Contracts, e-signatures, and deposits — all automated post-handshake.', tease: 'Negotiation guardrails built in. Close-lost analysis so you know why deals die. Deposit collected within 24 hours of signature.' },
        ]
    },
    {
        category: 'Operations',
        icon: Wrench,
        color: '#F59E0B',
        agents: [
            { id: '004', name: 'Crew Scheduling', tagline: 'Zero double-bookings. Skill-matched crews. Auto-routed.', tease: 'Integrates with Zuper. Zero crew idle time. Real-time weather monitoring baked in.' },
            { id: '005', name: 'Permitting', tagline: 'Permit apps filed in under 48 hours. Status tracked automatically.', tease: 'Looks up jurisdictional requirements, files, tracks, and alerts you on inspector scheduling. 0% missed close-outs.' },
            { id: '007', name: 'Material Management', tagline: 'Materials ordered from your own takeoffs. Supplier-compared. Tracked.', tease: 'Compare supplier pricing in real time. Zero material shortfalls on job day. Errors caught before crews arrive.' },
            { id: '011', name: 'Drone Planner', tagline: 'Flight path → 3D model → takeoff measurement. 30 minutes total.', tease: 'Zero human pilot errors. Automated pre/post-job aerial comparisons. Feed damage data directly to the insurance claim agent.' },
            { id: '012', name: 'Damage Assessment', tagline: 'AI image analysis: hail damage identified in under 5 minutes.', tease: 'Hail-trail overlay, repair vs. replace logic, and insurance documentation auto-generated from photos.' },
            { id: '015', name: 'Production Orchestrator', tagline: 'On-time completion rate target: 90%+. Job cycle: 21 days or less.', tease: 'Routes sub-agents for intake, crew assignment, weather monitoring, and homeowner confirmations.' },
            { id: '016', name: 'Crew Performance Analyzer', tagline: 'Know which crew installs fastest, gets fewest callbacks.', tease: 'Productivity by crew, callback rate tracking, and training recommendations. 10% crew productivity increase target over 6 months.' },
        ]
    },
    {
        category: 'Finance',
        icon: DollarSign,
        color: '#7B61FF',
        agents: [
            { id: '006', name: 'Invoicing & Collections', tagline: '100% of jobs invoiced within 24 hours of completion.', tease: 'Multi-milestone billing, progressive reminders, QuickBooks sync, and AR aging under 5% past 45 days.' },
            { id: '013', name: 'Estimate Generator', tagline: 'Good/better/best proposals generated in under 10 minutes.', tease: 'Profit margin optimization built in. Digital delivery with e-signature. Acceptance rate target: 50%.' },
            { id: '014', name: 'Insurance Claim Optimizer', tagline: 'Supplements filed. Appeals written. Average claim increase: 15%.', tease: 'Comprehensive documentation, code upgrade identification, adjuster communication. 75% supplement success rate target.' },
            { id: '018', name: 'Financial Forecaster', tagline: '30/60/90 day cash flow forecasts. Anomalies detected in 24 hours.', tease: 'Revenue pipeline analysis, budget vs actual variance, expense anomaly detection. Forecast accuracy within 10%.' },
        ]
    },
    {
        category: 'Marketing',
        icon: BarChart2,
        color: '#EC4899',
        agents: [
            { id: '008', name: 'Proactive Marketing', tagline: 'Storm hits. Agent activates within 2 hours. Campaigns deploy.', tease: 'Email drips, storm-triggered canvassing follow-up, referral program management. MQL pipeline never stops.' },
            { id: '010', name: 'Review Generation', tagline: '100% of completed jobs get a review request. 4.7+ star average.', tease: 'Multi-platform solicitation (Google, Yelp), automated response templates, negative review alerts within 2 hours.' },
            { id: '021', name: 'Paid Acquisition Optimizer', tagline: 'Google Ads ROAS target: 8x. Cost per qualified lead: under $250.', tease: 'Autonomous A/B testing of ad copy. Microsoft Clarity heatmap integration. Full-funnel ROI tracking.' },
            { id: '022', name: 'Platform Lead Orchestrator', tagline: 'LSA, Thumbtack, Porch, Directorii, Yelp — < 5 min response time.', tease: 'Cross-platform deduplication, lead credit/dispute management. 98% capture rate.' },
        ]
    },
    {
        category: 'Customer & Strategy',
        icon: Users,
        color: '#06B6D4',
        agents: [
            { id: '009', name: 'Customer Service Bot', tagline: '24/7 support. First response time: under 30 seconds.', tease: 'Project status lookups, warranty claim initiation, scheduling requests. 70%+ resolution rate without human escalation.' },
            { id: '017', name: 'Communication Hub', tagline: 'Inquiry calls drop 80%. Zero communication gaps over 3 days.', tease: 'Automated milestone notifications, personalized message generation, communication gap detection.' },
            { id: '020', name: 'Strategic Growth Advisor', tagline: 'Market trend analysis, competitive intel, scenario modeling. For you.', tease: 'Expansion recommendations, strategic scenario modeling for the CEO. 60%+ adoption rate for growth recommendations.' },
        ]
    },
];

const AgentEcosystem = () => {
    const [activeCategory, setActiveCategory] = useState(0);
    const [expandedAgent, setExpandedAgent] = useState(null);

    return (
        <section className="max-w-7xl mx-auto px-6 md:px-16 mb-32">
            <div className="scroll-reveal text-center mb-12">
                <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">Your Agent Ecosystem</p>
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                    22 agents. Five departments.{' '}
                    <span className="font-drama italic text-[#7B61FF]">One Mac Mini on your desk.</span>
                </h2>
                <p className="text-[#F0EFF4]/60 text-lg max-w-2xl mx-auto">
                    These are the types of agents you can build and operate through OpenClaw once it's installed. Each one is a glimpse into what becomes possible — not a tutorial.
                </p>
            </div>

            {/* Category tabs */}
            <div className="scroll-reveal flex flex-wrap gap-2 mb-8">
                {agentCategories.map((cat, i) => {
                    const IconEl = cat.icon;
                    return (
                        <button
                            key={i}
                            onClick={() => { setActiveCategory(i); setExpandedAgent(null); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all"
                            style={{
                                backgroundColor: activeCategory === i ? cat.color + '20' : 'transparent',
                                border: `1px solid ${activeCategory === i ? cat.color : '#F0EFF4'}20`,
                                color: activeCategory === i ? cat.color : '#F0EFF4' + '60',
                            }}
                        >
                            <IconEl size={14} />
                            {cat.category}
                        </button>
                    );
                })}
            </div>

            {/* Agent cards for active category */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {agentCategories[activeCategory].agents.map((agent, i) => {
                    const isExpanded = expandedAgent === i;
                    const cat = agentCategories[activeCategory];
                    return (
                        <div
                            key={i}
                            className="bg-[#18181B] border rounded-[1.5rem] p-6 cursor-pointer transition-all hover:shadow-lg"
                            style={{ borderColor: isExpanded ? cat.color + '50' : '#F0EFF420' }}
                            onClick={() => setExpandedAgent(isExpanded ? null : i)}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <span className="font-data text-xs font-bold tracking-widest" style={{ color: cat.color }}>
                                    AGENT {agent.id}
                                </span>
                                <ChevronDown
                                    size={16}
                                    className="text-[#F0EFF4]/30 transition-transform mt-1"
                                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }}
                                />
                            </div>
                            <h3 className="font-bold text-[#F0EFF4] text-lg mb-2">{agent.name}</h3>
                            <p className="text-[#F0EFF4]/60 text-sm">{agent.tagline}</p>

                            {isExpanded && (
                                <div className="mt-4 pt-4 border-t border-[#F0EFF4]/10">
                                    <p className="text-sm leading-relaxed" style={{ color: cat.color + 'CC' }}>
                                        {agent.tease}
                                    </p>
                                    <div className="mt-3 flex items-center gap-1 text-xs text-[#F0EFF4]/30">
                                        <Lock size={10} />
                                        Implementation detail is covered in your 3-hour training call.
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Unlock nudge */}
            <div className="scroll-reveal mt-10 text-center">
                <p className="text-[#F0EFF4]/40 text-sm max-w-lg mx-auto">
                    Every agent above is buildable with an OpenClaw installation. Your 3-hour Brain Melt session maps your business to your first 3 agents. Winning Wednesday evolves the rest.
                </p>
            </div>
        </section>
    );
};

// ─── AGENTIFICATION SECTION ───────────────────────────────────────────────────
// Shows the contrast between old-school Zapier automations and what an agent does
// Contagious: Unexpected + Practical Value ("news you can use")

const automationToAgent = [
    {
        oldLabel: 'Zapier: JobNimbus → Podium Review Request',
        oldDesc: 'A job status changes to "complete" → Podium sends a review SMS. Transactional. Dumb. Works once.',
        agentLabel: 'Agent 010: Review Generation',
        agentDesc: 'Monitors completion, reads the individual homeowner\'s communication history, personalizes the message, sends on optimal timing, monitors for response, auto-replies to reviews, and escalates negative ones within 2 hours.',
        multiplier: '12×',
    },
    {
        oldLabel: 'Zapier: CallRail → JobNimbus Speed-to-Lead Alert',
        oldDesc: 'A call comes in → creates a contact → sends a Slack alert to your team. They still have to respond manually.',
        agentLabel: 'Agent 001: Voice Lead Intake',
        agentDesc: 'Screens the caller intent, qualifies the lead against storm data, pulls up property history, schedules the inspection in Zuper, syncs to HubSpot, and follows up if the homeowner doesn\'t confirm — all without a human touch.',
        multiplier: '20×',
    },
    {
        oldLabel: 'Zapier: Acculynx → SendJim Neighbor Mailing',
        oldDesc: 'A job is sold → a batch postcard drops to neighbors. One-size-fits-all. Sent when the job is sold, which may be wrong timing.',
        agentLabel: 'Agent 008: Proactive Marketing',
        agentDesc: 'Monitors job status, cross-references hail trail data for the neighborhood, sequences neighbor outreach across email, SMS, and direct mail on the optimal day, adjusts for response rate in real time.',
        multiplier: '15×',
    },
    {
        oldLabel: 'Zapier: CompanyCam → JobNimbus Photo Sync',
        oldDesc: 'Photos taken in CompanyCam → synced to JobNimbus contact. Passive file management. No intelligence applied.',
        agentLabel: 'Agent 012: Damage Assessment',
        agentDesc: 'Receives photos, runs AI hail damage detection, generates a repair vs. replace recommendation, estimates claim value, drafts the insurance documentation, and feeds findings to the estimate generator agent automatically.',
        multiplier: '30×',
    },
];

const AgentificationSection = () => (
    <section className="max-w-7xl mx-auto px-6 md:px-16 mb-32">
        <div className="scroll-reveal text-center mb-16">
            <p className="font-data text-[#7B61FF] text-xs tracking-widest uppercase mb-4">Automations → Agents</p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
                You already run automations.{' '}
                <span className="font-drama italic text-[#7B61FF]">Imagine if they thought.</span>
            </h2>
            <p className="text-[#F0EFF4]/60 text-lg max-w-2xl mx-auto">
                The roofing industry has hundreds of Zapier-style automations: if this, then that. Useful. But fundamentally dumb — they can't adapt, escalate, personalize, or think. An agent can.
            </p>
        </div>

        <div className="space-y-6">
            {automationToAgent.map((item, i) => (
                <div key={i} className="scroll-reveal grid grid-cols-1 lg:grid-cols-5 gap-0 rounded-[2rem] overflow-hidden border border-[#7B61FF]/20">
                    {/* Old automation side */}
                    <div className="lg:col-span-2 bg-[#18181B] p-8">
                        <div className="font-data text-[#F0EFF4]/30 text-xs uppercase tracking-widest mb-3">Old Way</div>
                        <div className="font-bold text-[#F0EFF4]/70 text-sm mb-3">{item.oldLabel}</div>
                        <p className="text-[#F0EFF4]/40 text-xs leading-relaxed">{item.oldDesc}</p>
                    </div>
                    {/* Multiplier */}
                    <div className="lg:col-span-1 bg-[#0A0A14] flex items-center justify-center py-6 lg:py-0">
                        <div className="text-center">
                            <div className="font-drama text-[#7B61FF] text-4xl font-bold">{item.multiplier}</div>
                            <div className="font-data text-[#F0EFF4]/30 text-xs uppercase tracking-widest mt-1">more intelligence</div>
                        </div>
                    </div>
                    {/* Agent side */}
                    <div className="lg:col-span-2 bg-[#7B61FF]/10 border-l border-[#7B61FF]/30 p-8">
                        <div className="font-data text-[#7B61FF] text-xs uppercase tracking-widest mb-3">With an Agent</div>
                        <div className="font-bold text-[#F0EFF4] text-sm mb-3">{item.agentLabel}</div>
                        <p className="text-[#F0EFF4]/70 text-xs leading-relaxed">{item.agentDesc}</p>
                    </div>
                </div>
            ))}
        </div>

        <div className="scroll-reveal mt-12 text-center">
            <p className="text-[#F0EFF4]/40 text-sm max-w-xl mx-auto">
                The automations above are real workflows from the roofing industry's top operators. Every single one becomes exponentially more powerful when an agent replaces the "if/then" rule with actual intelligence, memory, and judgment.
            </p>
        </div>
    </section>
);

export default WhyBuyPage;
